import { useState, useEffect, useCallback } from 'react';

/**
 * Flags batches whose confirmation email never went out — processing was
 * started but the final step was missed. One click re-sends the exact email
 * that run would have produced (labels + counts + ship-by from the run date).
 */
export default function PendingEmailBanner({ refreshKey, secretKey, onNeedAuth, onAuthFailed }) {
  const [pending, setPending] = useState(null);
  const [sending, setSending] = useState(null); // batchDate currently sending
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    fetch('/api/notify/pending')
      .then((r) => r.json())
      .then((d) => setPending(d && !d.error ? d : null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const handleSend = async (group) => {
    if (!secretKey) {
      onNeedAuth();
      return;
    }
    setSending(group.batchDate);
    setError(null);
    try {
      const res = await fetch('/api/notify/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          secret: secretKey,
          batches: group.batches.map((b) => ({ label: b.label, orders: b.orders })),
          batchDate: group.batchDate,
        }),
      });
      const d = await res.json();
      if (res.status === 403) {
        onAuthFailed();
        return;
      }
      if (!res.ok) throw new Error(d.error || 'Failed to send');
      if (d.sent === false && d.skipped) throw new Error(d.skipped);
      if (d.sandboxed) {
        setError(`Sandbox send only — delivered to ${d.recipients.join(', ')}, so the batch stays flagged until a real send.`);
      }
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(null);
    }
  };

  if (!pending || pending.total === 0) return null;

  return (
    <div className="bg-red-50 border border-red-300 rounded-xl px-5 py-3.5 mb-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
          </div>
          <p className="text-sm text-red-900 font-medium">
            {pending.total} processed batch{pending.total !== 1 ? 'es' : ''} never had the confirmation email sent —
            production may not know to ship
          </p>
        </div>
      </div>
      <div className="mt-2.5 ml-9 space-y-1.5">
        {pending.groups.map((g) => (
          <div key={g.batchDate} className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-xs font-mono text-red-800">
              {g.batches.map((b) => `${b.label} (${b.orders})`).join(' · ')}
            </span>
            <button
              onClick={() => handleSend(g)}
              disabled={sending === g.batchDate}
              className="px-3 py-1 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 disabled:opacity-40 transition-colors"
            >
              {sending === g.batchDate ? 'Sending…' : 'Send Email Now'}
            </button>
          </div>
        ))}
      </div>
      {error && (
        <p className="text-[11px] text-red-800 bg-red-100 border border-red-200 rounded px-2 py-1 mt-2 inline-block">{error}</p>
      )}
    </div>
  );
}
